import React from 'react';
import { createBrowserHistory } from 'history';
import SnackbarUtils from './SnackbarUtils';
import {
  AcsAtaProfileType,
  locationDeploymentStatusNames,
  ORG_LEVEL,
  portRequestStatusNames,
  statusNames,
} from './constants';
import moment from 'moment-timezone';
import { jsPDF } from 'jspdf';
import * as yup from 'yup';
import axios from 'axios';
import { PERMISSIONROLECONSTANTS } from '../features/utils/utils.action';
import { CURRENT_STATUS } from '../types/enums/current-status.enum';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { requestStatusOptions } from '../types/enums/request-status.enum';
import { requestTypeOptions } from '../types/enums/request-type.enum';
import * as XLSX from 'xlsx';
import { FieldArrayMethodProps } from 'react-hook-form/dist/types/fieldArray';
import { GDMSDeviceAccountDetails } from '../types/ata/grandstream-ata/grandstream-ata.type';
import { UserType } from '../types/user/user.type';
import { EquipmentGroupType } from '../types/equipment-group/equipment-group.type';
import { LocationType } from '../types/location/location.type';
import { PBXType } from '../types/pbx/pbx.type';
import { ULARECentralStationType } from '../types/ulare/ulare.types';
import { ModelType } from '../types/inventory/inventory.types';
import { AddressType } from '../types/address/address.type';
import { ATAPortListItemType } from '../types/ata/ATAListItem.type';
import { formatDateTime } from './dateUtils';
import { PBXExtensionType } from '../types/pbx-extension/pbx-extension.type';

export const history = createBrowserHistory();
const phoneUtil = PhoneNumberUtil.getInstance();

export const getProviderOrganization = (org: any): any => {
  if (!org) return null;
  if (org.level === ORG_LEVEL.RESELLER || org.level === ORG_LEVEL.TELCLOUD) {
    return org;
  }

  if (!org.parent) {
    return null;
  }

  if (org.parent.level === ORG_LEVEL.RESELLER) {
    return org.parent;
  }

  return getProviderOrganization(org.parent);
};

export const generateCSVFile = (fileName: string, headers: string[], emptyRowsCount = 100) => {
  const emptyRows = Array.from({ length: emptyRowsCount }, () => ';'.repeat(headers.length - 1));
  const csvContent = [headers.join(';'), ...emptyRows].join('\n');
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const getDefaultOrganization = (
  selectedCompany: { id: number | 'all'; name: string } | null,
  user: null | UserType
): null | { id: number; name: string } => {
  if (!user?.organization) {
    return null;
  }

  return selectedCompany?.id === 'all'
    ? { id: user?.organization?.id, name: user?.organization?.name }
    : selectedCompany
    ? ({ id: selectedCompany.id, name: selectedCompany.name } as { id: number; name: string })
    : null;
};

// index starts from 0
export const parseGDMSAccountToString = (index: number, account: GDMSDeviceAccountDetails): string => {
  const port = account.fxsPortList.find(p => p.account === index + 1);
  return port ? `${port.sipUserId} (${port.sipServer})` : '-';
};

export const parseCounterToString = (
  c: {
    days: number;
    hours: number;
    minutes: number;
    seconds?: number;
  },
  customTo?: string,
  customAgo?: string
): string => {
  return `${c?.days !== 0 ? `${Math.abs(c?.days)} day${Math.abs(c.days) > 1 ? 's' : ''} ` : ''}${
    c?.days !== 0 || c?.hours !== 0 ? `${Math.abs(c?.hours)} hour${Math.abs(c.hours) > 1 ? 's' : ''} ` : ''
  }${
    c?.days !== 0 || c?.hours !== 0 || c?.minutes !== 0 || !Number.isNaN(c?.minutes)
      ? `${Math.abs(c?.minutes)} minute${Math.abs(c.minutes) > 1 ? 's' : ''} `
      : ''
  }${
    c.seconds !== undefined && (c?.days !== 0 || c?.hours !== 0 || c?.minutes !== 0 || c?.seconds !== 0)
      ? `${Math.abs(c?.seconds)} sec`
      : ''
  }${
    (c.seconds !== undefined && c.seconds < 0) || c.minutes < 0 || c.hours < 0 || c.days < 0
      ? customAgo
        ? customAgo
        : ' ago'
      : customTo
      ? customTo
      : ' to'
  }`;
};

export const parseCounterToCommentString = (c: {
  days: number;
  hours: number;
  minutes: number;
  seconds?: number;
}): string => {
  return `${c?.days !== 0 ? `${Math.abs(c?.days)} day${Math.abs(c.days) > 1 ? 's' : ''} ` : ''}${
    c?.days !== 0 || c?.hours !== 0 ? `${Math.abs(c?.hours)} hour${Math.abs(c.hours) > 1 ? 's' : ''} ` : ''
  }${
    c?.days !== 0 || c?.hours !== 0 || c?.minutes !== 0
      ? `${Math.abs(c?.minutes)} minute${Math.abs(c.minutes) > 1 ? 's' : ''}`
      : ''
  }${
    c.seconds !== undefined && (c?.days !== 0 || c?.hours !== 0 || c?.minutes !== 0 || c?.seconds !== 0)
      ? ` ${Math.abs(c?.seconds)} sec`
      : ''
  }`;
};

export const sortByName = (a: { name: string; id: number | string }, b: { name: string; id: number | string }) => {
  const nameA = a.name.toUpperCase();
  const nameB = b.name.toUpperCase();
  if (nameA < nameB) {
    return -1;
  }
  if (nameA > nameB) {
    return 1;
  }
  return 0;
};

export const parseCounterToStringShort = (c: {
  days: number;
  hours: number;
  minutes: number;
  seconds?: number;
}): string => {
  const hasAnyUnit =
    c?.days !== 0 || c?.hours !== 0 || c?.minutes !== 0 || (c.seconds !== undefined && c?.seconds !== 0);

  if (!hasAnyUnit) {
    return 'Just now';
  }

  return `${c?.days !== 0 ? `${Math.abs(c?.days)}d ` : ''}${
    c?.days !== 0 || c?.hours !== 0 ? `${Math.abs(c?.hours)}h ` : ''
  }${c?.days !== 0 || c?.hours !== 0 || c?.minutes !== 0 ? `${Math.abs(c?.minutes)}m ` : ''}${
    c.seconds !== undefined && (c?.days !== 0 || c?.hours !== 0 || c?.minutes !== 0 || c?.seconds !== 0)
      ? `${Math.abs(c?.seconds)}s`
      : ''
  }${(c.seconds !== undefined && c.seconds < 0) || c.minutes < 0 || c.hours < 0 || c.days < 0 ? ' ago' : ' to'}`;
};

export const readTree = (company, y, parent_id, bobo_enabled?, telcobill?) => {
  let ar: any = [];
  if (company) {
    if (company.level === ORG_LEVEL.RESELLER) {
      bobo_enabled = company.bobo_enabled;
      telcobill = company.telcobill;
    }

    ar.push({
      id: company.id,
      name: `${y} ${company.name}`,
      level: company.level,
      isTest: company.isTest,
      parent_id,
      bobo_enabled,
      telcobill,
    });
    if (company.children) {
      [...company.children].sort(sortByName).forEach(x => {
        ar = [...ar, ...readTree(x, y + '\t-', company.id, company.bobo_enabled || bobo_enabled, telcobill)];
      });
    } else return ar;
  }
  return ar;
};

export const countryCodes = [
  {
    country: 'USA',
    code: '+1',
    mask: ['+', '1', ' ', '(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
  },
];

export const isPhoneValid = (phone: string, country_code?: string): boolean => {
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone, country_code));
  } catch (error) {
    return false;
  }
};

export const getPotscastLineTypeOption = (profileType: AcsAtaProfileType) => {
  switch (profileType) {
    case AcsAtaProfileType.ALARM:
    case AcsAtaProfileType.ALARM_XMTR:
      return { id: AcsAtaProfileType.ALARM, name: 'Alarm' };
    case AcsAtaProfileType.ELEVATOR:
      return { id: AcsAtaProfileType.ELEVATOR, name: 'Elevator' };
    case AcsAtaProfileType.FAX:
    case AcsAtaProfileType.FAX_XMTR:
      return { id: AcsAtaProfileType.FAX, name: 'Fax' };
    case AcsAtaProfileType.MOIP:
      return { id: AcsAtaProfileType.MOIP, name: 'Modem' };
    default:
      return { id: AcsAtaProfileType.VOIP, name: 'Voice' };
  }
};

export const parsePhoneNumber = (phone: string, country_code?: string): string => {
  try {
    const number = phoneUtil.parseAndKeepRawInput(phone, (country_code || 'us').toLowerCase());
    return `${phoneUtil.format(number, 2)}`;
  } catch (e) {
    return phone;
  }
};

export const parsePhoneNumberWithDialCode = (phone: string, country_code?: string): string => {
  try {
    const number = phoneUtil.parseAndKeepRawInput(phone, (country_code || 'us').toLowerCase());
    return `${phoneUtil.format(number, 1)}`;
  } catch (e) {
    return phone;
  }
};

export const getDial = (phone: string, country_code?: string): number => {
  try {
    const number = phoneUtil.parseAndKeepRawInput(phone, (country_code || 'us').toLowerCase());
    return number.getCountryCode();
  } catch (e) {
    return 1;
  }
};

export const getCountryISO = (phone: string, country_code?: string): string => {
  try {
    const number = phoneUtil.parseAndKeepRawInput(phone, (country_code || 'us').toLowerCase());
    const regionCode = phoneUtil.getRegionCodeForNumber(number);

    return regionCode || (country_code || 'us').toUpperCase();
  } catch (e) {
    return (country_code || 'us').toUpperCase();
  }
};

export const parsePhoneNumberToRAW = (phone: string, country_code?: string): string => {
  try {
    const number = phoneUtil.parseAndKeepRawInput(phone, (country_code || 'us').toLowerCase());
    const countryCode = `+${number.getCountryCode()}`;
    return `${phoneUtil.format(number, 0)}`.replace(countryCode, '');
  } catch (e) {
    return phone;
  }
};

export const stateAndCountryValidate = data => {
  if (data.country?.states?.length > 0) {
    if (data['state'] && data.state.code) {
      data['state'] = data['state']['code'];
    }
  } else {
    data['state'] = data['state_text'];
  }
};

export const ipPattern =
  /^(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

export const macAddressMask = [
  /[a-fA-F0-9]/,
  /[a-fA-F0-9]/,
  ':',
  /[a-fA-F0-9]/,
  /[a-fA-F0-9]/,
  ':',
  /[a-fA-F0-9]/,
  /[a-fA-F0-9]/,
  ':',
  /[a-fA-F0-9]/,
  /[a-fA-F0-9]/,
  ':',
  /[a-fA-F0-9]/,
  /[a-fA-F0-9]/,
  ':',
  /[a-fA-F0-9]/,
  /[a-fA-F0-9]/,
];
export const twoCodeMask = [/[a-zA-Z]/, /[a-zA-Z]/];
export const threeCodeMask = [/[a-zA-Z]/, /[a-zA-Z]/, /[a-zA-Z]/];
export const fourCodeMask = [/[a-fA-F0-9]/, /[a-fA-F0-9]/, /[a-fA-F0-9]/, /[a-fA-F0-9]/];
export const sixCodeMask = [/[a-zA-Z0-9]/, /[a-zA-Z0-9]/, /[a-zA-Z0-9]/, /[a-zA-Z0-9]/, /[a-zA-Z0-9]/, /[a-zA-Z0-9]/];

export const generateNewQueryParams = (path, routeParams): any => {
  const settings = getSettings();
  const currentParams = Object.fromEntries(new URLSearchParams(path).entries());
  if (Object.prototype.hasOwnProperty.call(currentParams, 'T-Z')) {
    currentParams.carrier = currentParams.carrier + '&T-Z';
    delete currentParams['T-Z'];
  }
  const params = {};
  Object.keys(routeParams).forEach(key => {
    params[key] = typeof currentParams[key] === 'string' ? currentParams[key] : settings[key] || routeParams[key];
  });
  return params;
};

export const addressToString = (address: AddressType) => {
  return `${address.address1} ${address.address2} ${address.address3} \n${address.city} ${address.state} ${
    address.zip_code || address.zipCode
  } ${address.country?.code3 || address?.country?.code2 || ''}`;
};

export const locationToString = location => {
  return `${location.address1} ${location.address2} ${location.address3} \n${location.city} ${location.state} ${
    location.zip_code
  } ${location.country?.code3 || location?.country?.code2 || ''}`;
};

export const getOrganizationOptions: any = (company, y = '-', bobo_enabled?) => {
  let ar: Array<any> = [];
  if (company) {
    if (company.level === ORG_LEVEL.RESELLER) {
      bobo_enabled = company.bobo_enabled;
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ar.push({
      id: company.id,
      name: `${y} ${company.name}`,
      parent: company.parent,
      level: company.level,
      bobo_enabled: bobo_enabled,
      hasChildren: !!company.children?.length,
    });
    if (company.children) {
      company.children.forEach(x => {
        ar = [...ar, ...getOrganizationOptions(x, '\t' + y, company.bobo_enabled || bobo_enabled)];
      });
    } else return ar;
  }
  return ar;
};

export const checkEmptyValueInArray = (arr: string[]) => {
  let hasEmptyValue = false;
  arr.forEach(value => {
    if (!value) hasEmptyValue = true;
  });
  return hasEmptyValue;
};

export const checkEmptyAllValueInArray = (arr: string[]) => {
  let allValuseAreEmpty = true;
  arr.forEach(value => {
    if (value) allValuseAreEmpty = false;
  });
  return allValuseAreEmpty;
};

export const equalsArrays = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

export const emptyStringToNull = (value, originalValue) => {
  if (typeof originalValue === 'string' && originalValue === '') {
    return null;
  }
  return value;
};

export const trimString = (value, originalValue) => {
  if (typeof originalValue === 'string') {
    return originalValue.trim();
  }
  return value;
};

export const emptyStringToZero = (value, originalValue) => {
  if (typeof originalValue === 'string' && originalValue === '') {
    return 0;
  }
  return value;
};

export const nullToEmptyZero = (value, originalValue) => {
  if (originalValue === null) {
    return 0;
  }
  return value;
};

export const customSearchDropdownStyles = {
  control: (provided, state) => ({
    ...provided,
    borderColor: state.selectProps.invalid ? '#fc4b6c' : '#667287',
    borderRadius: '8px',
    height: '35px',
    boxShadow: 'none',
    ':focus': {
      borderColor: '#fc4b6c',
      boxShadow: '0 0 0 0.2rem rgb(252 75 108 / 25%)',
    },
    ':hover': {
      borderColor: state.selectProps.invalid ? '#fc4b6c' : '#667287',
    },
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    display: provided.display,
    color: state.selectProps.invalid ? '#fc4b6c' : provided.color,
  }),
  menu: (provided, state) => ({
    ...provided,
    width: '300px',
    zIndex: 10,
  }),
  menuList: (provided, state) => ({
    ...provided,
    '::-webkit-scrollbar': {
      width: '0px',
    },
  }),
  option: (provided, state) => ({
    ...provided,
    background: 'transparent',
    color: '#67757c',
    ':hover': {
      background: '#1e88e5',
      color: 'white',
    },
    whiteSpace: 'pre',
  }),
  placeholder: (provided, state) => ({
    ...provided,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  }),
  clearIndicator: (provided, state) => ({
    ...provided,
    display: provided.display,
  }),
  indicatorSeparator: (provided, state) => ({
    ...provided,
    display: provided.display,
    backgroundColor: provided.backgroundColor,
  }),
};

export const customDropdownStyles = {
  control: (provided, state) => ({
    ...provided,
    borderColor: state.selectProps.invalid ? '#fc4b6c' : '#667287',
    borderRadius: '2px',
    minHeight: '35px',
    // height: "35px",
    boxShadow: 'none',
    ':focus': {
      borderColor: '#fc4b6c',
      boxShadow: '0 0 0 0.2rem rgb(252 75 108 / 25%)',
    },
    ':hover': {
      borderColor: state.selectProps.invalid ? '#fc4b6c' : '#667287',
    },
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: state.selectProps.invalid ? '#fc4b6c' : provided.color,
  }),
  menu: provided => ({ ...provided, zIndex: 10 }),
  option: (provided, state) => ({
    ...provided,
    background: 'transparent',
    color: state.isDisabled ? '#c2c2c2' : '#5e5e5e',
    backgroundColor: state.isDisabled ? '#aaaaaa' : 'white',
    cursor: state.isDisabled ? 'not-allowed' : 'default',
    ':hover': {
      background: '#1e88e5',
      color: 'white',
    },
  }),
  indicatorSeparator: (provided, state) => ({
    ...provided,
    backgroundColor: state.selectProps.invalid ? '#fc4b6c' : provided.backgroundColor,
  }),
};

export const customAsyncDropdownStyles = {
  control: (provided, state) => ({
    ...provided,
    borderColor: state.selectProps.invalid ? '#fc4b6c' : '#e9ecef',
    borderRadius: '2px',
    minHeight: '35px',
    // height: "35px",
    boxShadow: 'none',
    ':hover': {
      borderColor: state.selectProps.invalid ? '#fc4b6c' : 'rgba(0, 0, 0, 0.25)',
    },
    ':focus': {
      transition: 'all 0.3s ease-in-out',
      borderColor: state.selectProps.invalid ? '#fc4b6c' : 'rgba(0, 0, 0, 0.25)',
      boxShadow: '0 0 0 0.2rem rgb(252 75 108 / 25%)',
    },
  }),
  menu: provided => ({ ...provided, zIndex: 10 }),
  option: (provided, state) => ({
    ...provided,
    background: 'transparent',
    color: state.isDisabled ? '#c2c2c2' : '#5e5e5e',
    backgroundColor: state.isDisabled ? '#aaaaaa' : 'white',
    cursor: state.isDisabled ? 'not-allowed' : 'default',
    ':hover': {
      background: '#1e88e5',
      color: 'white',
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: () => ({
    display: 'none',
  }),
};

export const getSettings = (): any => {
  let settings: any = localStorage.getItem('settings') || null;
  try {
    if (settings && JSON.parse(settings)) {
      settings = JSON.parse(settings);
    } else {
      settings = {};
    }
  } catch {
    settings = {};
  }
  return settings;
};

export const flattenRoutes = obj => {
  let arr: Array<any> = [];
  delete obj.child;
  obj.forEach(o => {
    if (!Object.prototype.hasOwnProperty.call(o, 'child')) {
      arr.push(o);
    } else {
      arr = [...arr, ...flattenRoutes(o.child)];
    }
  });
  return arr;
};

export const generateUPSTrackingLink = (tn: string) => `https://www.ups.com/track?tracknum=${tn}`;
export const generateFedExTrackingLink = (tn: string) => `https://www.fedex.com/fedextrack/?tracknumbers=${tn}`;

export const generateTrackingLink = (tn: string, carrier?: string): string => {
  const tnLower = (tn || '').trim().toLowerCase();
  const carrierLower = (carrier || '')?.trim().toLowerCase();
  if (tnLower.includes('pending') || tnLower.includes('transfer') || tnLower.includes('test')) {
    return '';
  }

  if (carrierLower === 'ups') {
    return generateUPSTrackingLink(tn);
  }

  if (carrierLower === 'fedex') {
    return generateFedExTrackingLink(tn);
  }

  return '';
};

export const getTimezoneList = () => {
  const timezones = moment.tz.names();
  const obj: Array<{ label: string; value: string }> = [];
  timezones.forEach(t => {
    obj.push({ label: `${t.replaceAll('_', ' ')} (${moment.tz(t).format('Z')})`, value: t });
  });
  return obj;
};

export const numberToDateFormat = (number, format) => {
  return moment(new Date(number)).format(format);
};

export const printEquipmentGroupInformations = (equipments, locationId) => {
  const doc = new jsPDF({
    unit: 'mm',
    orientation: 'landscape',
    format: [89, 28],
  });

  equipments.forEach((equipment, i) => {
    const qrCode: any = document.getElementById(`qr-code-to-print-${equipment.id}`);
    if (qrCode) {
      // QR page
      const imgData = qrCode.toDataURL('image/png');
      doc.setFontSize(14);
      doc.text(`L-${locationId}`, 7, 13);
      doc.text(`EG-${equipment.id}`, 7, 17);
      doc.addImage(imgData, 'JPEG', 55, 4, 20, 20);

      // router pages
      if (equipment.routers.length) {
        equipment.routers.forEach(router => {
          // Title
          doc.addPage([89, 28], 'landscape');
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          doc.text('ROUTER:', 7, 6);
          doc.text('SN:', 7, 10);
          doc.setFont('helvetica', 'normal');
          doc.text(`${router.model || 'N/A'}`, 24, 6);
          doc.text(`${router.serial_number || 'N/A'}`, 14, 10);

          // Interfaces
          doc.setFontSize(6);
          if (router.router_interfaces.length) {
            router.router_interfaces.forEach((router_interface, i) => {
              doc.setFont('helvetica', 'bold').text(`${router_interface.name}`, 7 + i * 40, 13);
              doc.setFont('helvetica', 'bold').text('IMEI:', 7 + i * 40, 15);
              doc.setFont('helvetica', 'normal').text(`${router_interface.imei || 'N/A'}`, 13 + i * 40, 15);
              if (router_interface.sims.length) {
                router_interface.sims.forEach((sim, j) => {
                  doc.setFont('helvetica', 'bold').text(`SIM #${j + 1}:`, 7 + i * 40, 17 + j * 2);
                  doc.setFont('helvetica', 'normal').text(`${sim.iccid || 'N/A'}`, 15 + i * 40, 17 + j * 2);
                });
              }
            });
          } else {
            doc.setFont('helvetica', 'bold');
            doc.text('N/A', 7, 13);
            doc.text('IMEI:', 7, 15);
            doc.text('SIM #1:', 7, 17);
            doc.text('SIM #2:', 7, 19);
            doc.setFont('helvetica', 'normal');
            doc.text(' N/A', 12, 15);
            doc.text('N/A', 15, 17);
            doc.text('N/A', 15, 19);
          }
        });
      }
      // ATA page
      if (equipment.atas.length) {
        equipment.atas.forEach(ata => {
          // title
          doc.addPage([89, 28], 'landscape');
          doc.setFontSize(8);
          doc.setFont('helvetica', 'bold').text('MAC:', 7, 6);
          doc.setFont('helvetica', 'normal').text(`${ata.mac || 'N/A'}`, 15, 6);
          doc.setFont('helvetica', 'bold').text('SN:', 45, 6);
          doc.setFont('helvetica', 'normal').text(`${ata.serial_number || 'N/A'}`, 50, 6);

          // SIMS
          doc.setFontSize(6);
          for (let i = 0; i < 8; i++) {
            const x = i < 4 ? 7 : 45;
            const y = i < 4 ? 10 + i * 4 : 10 + (i - 4) * 4;
            const pbxExtension = ata.ata_ports[i]?.pbxExtension || null;
            const ata_org_template = ata.ata_org_template || null;
            doc.setFont('helvetica', 'bold').text(`L${i + 1}:`, x, y);
            doc
              .setFont('helvetica', 'normal')
              .text(
                `${
                  pbxExtension
                    ? `${pbxExtension?.username} ${
                        ata_org_template && Number.isInteger(ata.ata_ports[i]?.profile_id)
                          ? `(${ata_org_template[`profile${ata.ata_ports[i].profile_id + 1}`]})`
                          : ''
                      }`
                    : 'N/A'
                }`,
                x + 4,
                y
              );
          }
        });
      }

      // ATA ACS page
      if (equipment.acs_atas?.length) {
        equipment.acs_atas.forEach(ata => {
          // title
          doc.addPage([89, 28], 'landscape');
          doc.setFontSize(8);
          doc.setFont('helvetica', 'bold').text('MAC:', 7, 6);
          doc.setFont('helvetica', 'normal').text(`${ata.mac || 'N/A'}`, 15, 6);
          doc.setFont('helvetica', 'bold').text('SN:', 45, 6);
          doc.setFont('helvetica', 'normal').text(`${ata.serial_number || 'N/A'}`, 50, 6);

          // Build port map by fxs_port
          doc.setFontSize(6);
          [...ata.acs_ata_ports]
            .sort((a, b) => a.fxs_port - b.fxs_port)
            .forEach((port, k) => {
              if (k !== 0 && k % 8 === 0) {
                doc.addPage([89, 28], 'landscape');
              }
              const newK = k % 8;

              const x = newK < 4 ? 7 : 45;
              const y = newK < 4 ? 10 + newK * 3 : 10 + (newK - 4) * 3;
              const pbxExtension = port?.pbxExtension || null;
              const profile = port?.profile?.name || null;
              doc.setFont('helvetica', 'bold').text(`L${k + 1}:`, x, y);
              doc
                .setFont('helvetica', 'normal')
                .text(
                  `${pbxExtension ? `${pbxExtension?.username} ${profile ? `(${profile})` : ''}` : 'N/A'}`,
                  k + 1 > 9 ? x + 5 : x + 4,
                  y
                );
            });
        });
      }
    }
    if (i < equipments.length - 1) {
      doc.addPage([89, 28], 'landscape');
    }
  });
  if (equipments.length === 1) {
    doc.save(`L${locationId}EG${equipments[0].id}.pdf`);
  } else {
    doc.save(`L${locationId}.pdf`);
  }
};

export const warehousePayloadValidate = payload => {
  ['location', 'organization'].forEach(x => {
    delete payload[x];
  });
};

export const getModelTypeParam = (model: ModelType) => {
  let type = '';

  if (model.isAta) type = 'isAta';
  if (model.isBattery) type = 'isBattery';
  if (model.isDialer) type = 'isDialer';
  if (model.isEnclosure) type = 'isEnclosure';
  if (model.isSim) type = 'isSim';
  if (model.isRouter) type = 'isRouter';
  if (model.isPowerCycle) type = 'isPowerCycle';

  return type;
};

export const inventoryDataPrepare = data => {
  data['organization'] = data.equipment_group?.location?.organization || null;
  data['equipment_group'] = data.equipment_group || null;
  data['location'] = data.equipment_group?.location || null;
  return data;
};

export const yupEmailValidation = () =>
  yup
    .string()
    .matches(
      RegExp(
        '^[a-zA-Z0-9._-]+(\\+?[a-zA-Z0-9._-]+)?@[a-zA-Z0-9]+([a-zA-Z0-9._-]*[a-zA-Z0-9])?\\.[a-zA-Z]{2,}(\\.[a-zA-Z]{2,})?$'
      ),
      'Wrong email address'
    )
    .transform(emptyStringToNull)
    .nullable(true);

export const additionalQueryParams = (organization, location, includeSuborgs, country?: any) => {
  const params = {};
  if (organization && organization.id !== 'all') {
    params['org_id'] = organization.id;
    // url += `&org_id=${organization.id}`;
  }
  // else {
  //   params['inc_sub'] = true;
  //   // url += `&inc_sub=true`;
  // }

  if (location && location.id !== 'all' && +location.id) {
    params['location_id'] = location.id;
    // url += `&location_id=${location.id}`;
  }

  params['inc_sub'] = includeSuborgs && !Object.prototype.hasOwnProperty.call(params, 'inc_sub');

  if (country) {
    params['country_id'] = country.id;
  }

  return params;
};

export const isSuperadminOrTelcloudAdmin = user => {
  return user.role === PERMISSIONROLECONSTANTS.superadmin || user.role === PERMISSIONROLECONSTANTS.telcloud_admin;
};

export const getColumnsCSVHeaders = (
  columns: Array<{ Header?: any; name?: string; csvKey?: string }>
): Array<{ name: string; key: string }> => {
  return columns.reduce<Array<{ name: string; key: string }>>((acc, col) => {
    if (!col.csvKey) return acc;
    const displayName =
      typeof col.Header === 'string' && col.Header.trim()
        ? col.Header
        : typeof col.name === 'string' && col.name.trim()
        ? col.name
        : '';
    if (displayName) acc.push({ name: displayName, key: col.csvKey });
    return acc;
  }, []);
};

export const generateCSVListFile = (
  headers: Array<{ name: string; key: string; csvTransform?: (row: any) => any }>,
  list: Array<any>,
  name: string
) => {
  let csvContent = 'data:text/csv;charset=utf-8,';
  headers.forEach((header, i) => {
    csvContent += `${header.name}`;
    if (i < headers.length - 1) csvContent += ',';
  });
  csvContent += '\n';

  list.forEach(item => {
    headers.forEach((header, i) => {
      if (header.csvTransform) {
        const transformed = header.csvTransform(item);
        csvContent += `"${transformed ?? ''}"`;
        if (i < headers.length - 1) csvContent += ',';
        return;
      }
      const objKeys = header.key.split('.');
      let value = item;
      objKeys.forEach(k => {
        if (value !== undefined && value !== null) {
          if (typeof value[k] === 'number') {
            value = value[k];
          } else if (typeof value[k] === 'boolean') {
            value = value[k];
          } else {
            value = value[k] || null;
          }
        }
      });

      if (header.key === 'sim') {
        const sim = item?.tc_sim;
        const simString = sim ? `${sim.iccid} ${sim.iccid && sim.eid ? '-' : ''} ${sim.eid}` : '';
        csvContent += `"${simString}"`;
      } else if (header.key === 'did.provider') {
        csvContent += `"${item.telco_instance?.provider || item.provider || ''}"`;
      } else if (header.key === 'did.profile') {
        const ataProfileId = item.ata_port?.profile_id;
        const ataProfile =
          ataProfileId === 0
            ? item?.ata_port?.ata?.ata_org_template?.profile1
            : ataProfileId === 1
            ? item?.ata_port?.ata?.ata_org_template?.profile2
            : '';
        const acsProfile = item.acs_ata_port?.profile?.name;
        csvContent += `"${acsProfile || ataProfile || ''}"`;
      } else if (header.key === 'address') {
        csvContent += `"${item.address?.['address1'] || item['address1'] || ''} ${
          item.address?.['address2'] || item['address2'] || ''
        } ${item.address?.['address3'] || item['address3'] || ''}"`;
      } else if (header.key === 'changes') {
        csvContent += '"';
        item['changes'].forEach((change, i) => {
          csvContent += `${i + 1}. ${change || '-'}\n`;
        });
        csvContent += '"';
      } else if (header.key === 'imei') {
        let interfaces = item.router_interfaces ? item.router_interfaces : [];
        if (item.imei) interfaces = [{ imei: item.imei }, ...interfaces];
        interfaces.forEach(i => {
          csvContent += `${i.imei || ''}`;
        });
      } else if (header.key === 'current_status') {
        csvContent += `"${statusNames[value] || ''}"`;
      } else if (header.key === 'deployment_status') {
        csvContent += `"${locationDeploymentStatusNames[value] || ''}"`;
      } else if (header.key === 'port_request_status') {
        csvContent += `"${portRequestStatusNames[value] || ''}"`;
      } else if (header.key === 'requestStatus') {
        csvContent += `"${requestStatusOptions.find(o => o.id === value)?.name || ''}"`;
      } else if (header.key === 'requestType') {
        csvContent += `"${requestTypeOptions.find(o => o.id === value)?.name || ''}"`;
      } else if (header.key === 'notInTelco' || header.key === 'notInRevio' || header.key === 'isDifferent') {
        csvContent += item[header.key]?.length > 0 ? `"${item[header.key].join('\n')}"` : '"All Verified"';
      } else if (header.key === 'organization.name') {
        csvContent += `"${
          item?.organization?.name ||
          item?.equipment_group?.location?.organization?.name ||
          item?.location?.organization?.name ||
          ''
        }"`;
      } else if (header.key === 'dialer.organization.name') {
        csvContent += `"${
          item?.dialer?.organization?.name || item?.dialer?.equipment_group?.location?.organization?.name || ''
        }"`;
      } else if (header.key === 'equipment_group.location.name') {
        csvContent += `"${item?.location?.name || item?.equipment_group?.location?.name || ''}"`;
      } else if (header.key === 'sim.organization.name') {
        csvContent += `"${
          item?.sim?.equipment_group?.location?.organization?.name ||
          item?.sim?.router?.equipment_group?.location?.organization?.name ||
          ''
        }"`;
      } else if (header.key === 'cdr.location.name') {
        const location =
          item['did']?.acs_ata_port?.ata?.equipment_group?.location?.name ||
          item['did']?.ata_port?.ata?.equipment_group?.location?.name ||
          item['did']?.pbx?.location?.name ||
          '';
        csvContent += `"${location.replace(/#/g, '').replace(/"/g, "'").replace(/"/g, "'")}"`;
      } else if (header.key === 'power_cycle.power_cycle_channels') {
        csvContent += '"';
        if (item?.power_cycle_channels?.length > 0) {
          item?.power_cycle_channels.forEach((channel, j) => {
            csvContent += `${j + 1}. ${
              channel.ata?.serial_number ||
              channel.acs_ata?.serial_number ||
              channel.dialer?.radio_id ||
              channel.router?.serial_number ||
              '-'
            } ${channel?.device_type ? `(${channel?.device_type})` : ''}\n`;
          });
        }
        csvContent += '"';
      } else if (header.key === 'cdr.equipment_group.description') {
        const equipment_group =
          item['did']?.acs_ata_port?.ata?.equipment_group?.location?.name ||
          item['did']?.ata_port?.ata?.equipment_group?.location?.name ||
          item['did']?.pbx?.location?.name ||
          '';
        csvContent += `"${equipment_group.replace(/#/g, '').replace(/"/g, "'").replace(/"/g, "'")}"`;
      } else if (header.key === 'notification.status') {
        let status = '';
        if (item.current_status === CURRENT_STATUS.GREEN) status = 'Up';
        if (item.current_status === CURRENT_STATUS.RED) status = 'Down';

        csvContent += `"${status}"`;
      } else if (header.key === 'notification.type') {
        let type = '';
        if (item.webhook_url) type = 'Webhook';
        if (item.verified_email) type = 'Email';
        if (item.verified_number) type = 'SMS';
        csvContent += `"${type}"`;
      } else if (header.key === 'did.location.name') {
        const location =
          item['acs_ata_port']?.ata?.equipment_group?.location?.name ||
          item['ata_port']?.ata?.equipment_group?.location?.name ||
          item['pbx']?.location?.name ||
          '';
        csvContent += `"${location.replace(/#/g, '').replace(/"/g, "'").replace(/"/g, "'")}"`;
      } else if (header.key === 'did.ata.mac') {
        csvContent += `"${item['acs_ata_port']?.ata?.mac || item['ata_port']?.ata?.mac || ''}"`;
      } else if (header.key.includes('ata_pbxs')) {
        const ataPorts: ATAPortListItemType[] = item.ports.sort((a, b) => a.fxsPort - b.fxsPort) || [];

        const value = ataPorts
          .map((port, j) => {
            return `${j + 1}. ${port?.pbx?.description || '-'}`;
          })
          .join('\n');
        csvContent += `"${value}"`;
      } else if (header.key.includes('ata_ports')) {
        const ataPorts: ATAPortListItemType[] = item.ports.sort((a, b) => a.fxsPort - b.fxsPort) || [];

        const value = ataPorts
          .map((port, j) => {
            return `${j + 1}. ${port?.did?.did || '-'} ${
              port?.did?.did && port?.profile ? `(${port?.profile.name})` : ''
            }`;
          })
          .join('\n');
        csvContent += `"${value}"`;
      } else if (header.key === 'ata_telco_blocks_descriptions') {
        const ataPorts: ATAPortListItemType[] = item.ports.sort((a, b) => a.fxsPort - b.fxsPort) || [];

        const value = ataPorts
          .map((port, j) => {
            return `${j + 1}. ${port.description || '-'}`;
          })
          .join('\n');
        csvContent += `"${value}"`;
      } else if (header.key.includes('ata_telco_blocks')) {
        const ataPorts: ATAPortListItemType[] = item.ports.sort((a, b) => a.fxsPort - b.fxsPort) || [];

        const value = ataPorts
          .map((port, j) => {
            return `${j + 1}. ${port.telcoBlock?.name || '-'}`;
          })
          .join('\n');
        csvContent += `"${value}"`;
      } else if (header.key === 'user.status') {
        const user = item;
        const todayDate: Date = new Date();
        const lastActionDate = user.last_action ? new Date(user.last_action) : null;
        const dateDiff = lastActionDate ? +todayDate - +lastActionDate : null;
        const status = !user.is_active ? 'inactive' : dateDiff && dateDiff <= 900000 ? 'online' : 'offline';
        csvContent += `"${status}"`;
      } else if (header.key === 'ticket.createdAt') {
        csvContent += `"${formatDateTime(item.createdAt, 'EEE, MM/dd/yyyy hh:mm a')}"`;
      } else if (header.key === 'ticket.techOnSite') {
        csvContent += `"${Boolean(item.techName)}"`;
      } else if (typeof value === 'string') {
        if (header.key === 'createdAt') {
          const formatted = value ? moment(new Date(value)).format('MM-DD-YYYY') : '';
          csvContent += `"${formatted}"`;
        } else {
          csvContent += `"${value.replace(/#/g, '').replace(/"/g, "'").replace(/"/g, "'")}"`;
        }
      } else if (typeof value === 'object' || value?.length > 0) {
        csvContent += '"';
        if (header.key === 'order_tracking_info') {
          const tracking_numbers = item.tracking_numbers || [];
          value.forEach((info, j) => {
            csvContent += `${j + 1}. ${info?.number || '-'}\n`;
          });
          if (value?.length === 0) {
            tracking_numbers.forEach((number, j) => {
              csvContent += `${j + 1}. ${number || '-'}\n`;
            });
          }
        } else if (header.key === 'trackingNumbers') {
          (value || []).forEach((v, j) => {
            csvContent += `${j + 1}. ${v || '-'}\n`;
          });
        } else if (header.key === 'shippingDate') {
          (value || []).forEach((v, j) => {
            const formatted = v ? moment(new Date(v)).format('MM-DD-YYYY') : '-';
            csvContent += `${j + 1}. ${formatted}\n`;
          });
        } else if (header.key === 'shipCarrier') {
          const carriers = item?.shippingCarrier as any[];
          carriers.forEach((v, j) => {
            csvContent += `${j + 1}. ${v || '-'}\n`;
          });
        } else if (header.key === 'items') {
          const items = (value || []) as any[];
          items.forEach((item, j) => {
            const itemType = item.model?.isAta
              ? 'ATA'
              : item.model?.isBattery
              ? 'Battery'
              : item.model?.isDialer
              ? 'Dialer'
              : item.model?.isEnclosure
              ? 'Enclosure'
              : item.model?.isPowerCycle
              ? 'Power Cycle'
              : item.model?.isRouter
              ? 'Router'
              : item.model?.isSim
              ? 'SIM'
              : 'Device';
            csvContent += `${j + 1}. ${itemType} - ${item.model?.brand?.name || 'N/A'} ${
              item.model?.name || ''
            } (Qty: ${item.quantity || 0})\n`;
          });
        }
        if (header.key === 'port_request_dids') {
          item['port_request_dids'].forEach((port, j) => {
            csvContent += `${j + 1}. ${port?.did || '-'}\n`;
          });
        }
        if (header.key === 'area_of_service') {
          item['areas'].forEach((areaOfService, j) => {
            csvContent += `${areaOfService.country?.code3} - ${areaOfService.city} - ${areaOfService.zipCode}\n`;
          });
        }
        if (header.key === 'router_interfaces') {
          item['router_interfaces'].forEach((router_interface, j) => {
            if (header.name === 'Interface APN') {
              csvContent += `${j + 1}. ${router_interface.apn || '-'}\n`;
            }
            if (header.name === 'Carrier') {
              csvContent += `${j + 1}. ${router_interface.carrier_name || '-'}\n`;
            }
            if (header.name === 'Interface IMEI') {
              csvContent += `${j + 1}. ${router_interface.imei || '-'}\n`;
            }
          });
        }
        if (header.key === 'router_signal') {
          item['router_interfaces'].forEach((router_interface, j) => {
            const { name, rssi, sinr, rsrq, rsrp } = router_interface;
            csvContent += `${j + 1}. ${name || '-'}`;
            csvContent += ` RSSI: ${rssi || '-'},`;
            csvContent += ` SINR: ${sinr || '-'},`;
            csvContent += ` RSRQ: ${rsrq || '-'},`;
            csvContent += ` RSRP: ${rsrp || '-'}\n`;
          });
        }
        if (header.key === 'sims') {
          item['router_interfaces'].forEach((router_interface, j) => {
            csvContent += `${j + 1}. `;
            if (header.name === 'ICCID') {
              csvContent += `${router_interface['sims'].map(sim => sim.iccid).join(', ')}`;
            }
            if (header.name === 'IMSI') {
              csvContent += `${router_interface['sims'].map(sim => sim.imsi || '-').join(', ')}`;
            }
            if (header.name === 'MTN') {
              csvContent += `${router_interface['sims'].map(sim => sim.mtn || '-').join(', ')}`;
            }
            if (header.name === 'Sim APN') {
              csvContent += `${router_interface['sims'].map(sim => sim.apn || '-').join(', ')}`;
            }
            csvContent += '\n';
          });
        }
        csvContent += '"';
      } else if (typeof value === 'boolean') {
        csvContent += `"${value}"`;
      } else if (typeof value === 'number') {
        csvContent += `"${value}"`;
      } else {
        csvContent += `"${value || ''}"`;
      }

      if (i < headers.length - 1) csvContent += ',';
    });
    csvContent += '\n';
  });
  csvContent = csvContent.replaceAll('#', '');
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement('a');
  link.setAttribute('href', encodedUri);
  link.setAttribute('download', `${name}-list.csv`);
  document.body.appendChild(link);
  link.click();
};

export const getResultsFromAPI = async (
  path: string,
  params: any,
  results: Array<any>,
  options?: {
    fetchAllPages?: boolean;
    filterEmpty?: boolean;
    override?: Record<string, unknown>;
  }
) => {
  const filterParams = (p: Record<string, unknown>) =>
    Object.keys(p || {}).reduce((acc: Record<string, unknown>, key) => {
      const v = (p as any)[key];
      if (!options?.filterEmpty || (v !== '' && v !== null && v !== undefined)) {
        acc[key] = v;
      }
      return acc;
    }, {});

  const prepareParams = () => {
    const base = filterParams(params || {});
    const ov = filterParams((options?.override as Record<string, unknown>) || {});
    return { ...base, ...ov } as any;
  };

  const fetchAll = options?.fetchAllPages !== false;
  const finalParams = prepareParams();

  const doRequest = async (p: any) => {
    const response = await axios.get(`${path}`, { params: p });
    return response.data?.items || [];
  };

  if (!fetchAll) {
    const items = await doRequest(finalParams);
    return items;
  }

  let items = await doRequest(finalParams);
  results = [...results, ...items];

  while (items.length === finalParams.limit) {
    finalParams.page += 1;
    const nextParams = { ...finalParams, page: finalParams.page as number };
    items = await doRequest(nextParams);
    results = [...results, ...items];
  }

  return results;
};

export const groupBy = (xs, key) => {
  return xs.reduce(function (rv, x) {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {});
};

export const groupStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

export const groupBadgeStyles = {
  backgroundColor: '#EBECF0',
  borderRadius: '2em',
  color: '#172B4D',
  display: 'inline-block',
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '1',
  minWidth: 1,
  padding: '0.16666666666667em 0.5em',
  textAlign: 'center',
};

export const formatGroupLabel = data => {
  return (
    <div style={groupStyles}>
      {data?.name === '+ Create new' ? (
        <>
          <span>{data?.name && data.name !== 'null' ? data.name : 'Not Categorized'}</span>
        </>
      ) : (
        <>
          <span>{data?.name && data.name !== 'null' ? data.name : 'Not Categorized'}</span>
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore */}
          <span style={groupBadgeStyles}>{data.options.length}</span>
        </>
      )}
    </div>
  );
};

export const mergeTree = (item: any, key: string): Array<any> => {
  let ar: Array<any> = [];
  if (item) {
    ar.push({ ...item });

    if (item[key] && item[key].length > 0) {
      item[key].forEach(x => {
        const result = mergeTree(x, key);
        ar = [...ar, ...result];
      });
    } else return ar;
  }
  return ar;
};

export const downloadTemplateXLSX = (headers: Array<string>, name: string) => {
  const rows: string[][] = [];
  rows.push(headers);
  for (let i = 0; i < 100; i++) {
    rows.push(new Array(headers.length).fill(''));
  }

  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  worksheet['!cols'] = Array(headers.length).fill({ wch: 50 });
  for (const cell in worksheet) {
    // eslint-disable-next-line no-prototype-builtins
    if (worksheet.hasOwnProperty(cell) && cell[0] !== '!') {
      worksheet[cell].z = '@';
    }
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
  XLSX.writeFile(workbook, `${name}.xlsx`);
};

export const formatEquipmentGroupLabel = (option: EquipmentGroupType) => {
  if (!option || !option.description) return '';
  return `(${option.id}) ${option.description}`;
};

export const formatPBXLabel = (option: PBXType) => {
  if (!option || !option.description) return '';
  return `${option.description}`;
};

export const formatPBXLabelWithAvailableDIDs = (option: PBXType & { availableDIDsCount?: number }) => {
  if (!option || !option.description) return '';
  return `${option.description} ${
    option?.availableDIDsCount !== undefined ? `(${option?.availableDIDsCount || 0})` : ''
  }`;
};

export const formatPBXExtensionLabel = (option: PBXExtensionType) => {
  if (!option || !option.username) return '';
  return option.username || option.description;
};

export const formatCentralStationLabel = (option: ULARECentralStationType) => {
  if (!option || !option.description) return '';
  return `${option.description}`;
};

export const formatLocationLabel = (option: LocationType) => {
  if (!option || !option.name) return '';
  return `(${option.id}) ${option.name}`;
};

export const convertExcelToCSV = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const csv = XLSX.utils.sheet_to_csv(worksheet);
        resolve(csv);
      } catch (error) {
        reject(new Error('Failed to convert Excel file to CSV'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read Excel file'));
    reader.readAsBinaryString(file);
  });
};

export const onDropFile = (
  files: File[],
  headers: Array<string>,
  append: (rows: any, options?: FieldArrayMethodProps) => void
): void => {
  const reader = new FileReader();
  reader.onload = function (e) {
    if (!e.target || !e.target.result) {
      SnackbarUtils.error('Failed to read the file. Please try again.');
      return;
    }
    const binaryStr = e.target.result;
    try {
      const workbook = XLSX.read(binaryStr, { type: 'binary', raw: true });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      if (!jsonData || jsonData.length === 0) {
        SnackbarUtils.info('Imported file is empty or corrupted!');
        return;
      }

      const h = jsonData[0];
      const r = jsonData.slice(1);

      let headerError = false;
      if (!h || (h as Array<string>)?.length !== headers.length) {
        headerError = true;
      }

      (h as Array<string>).forEach((header, index) => {
        if (header.replace(/\s/g, '') !== headers[index].replace(/\s/g, '')) {
          headerError = true;
        }
      });

      if (headerError) {
        SnackbarUtils.info('Imported file has incorrect headers!');
      } else {
        // PARSE
        const objects = (r as Array<Array<string>>)
          .filter(record => record.some(value => value && value.toString().trim() !== ''))
          .map(record => {
            const obj = {};
            headers.forEach((header, index) => {
              obj[header] = record[index] ? record[index].toString().trim() : '';
            });
            return obj;
          });

        if (objects.length === 0) {
          SnackbarUtils.info('No valid rows found in the uploaded file.');
          return;
        }

        append(objects);
      }
    } catch (err) {
      SnackbarUtils.error('Failed to process the uploaded file.');
    }
  };

  reader.readAsBinaryString(files[0]);
};
